import { TaxTypeIndia } from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  axiosInstance,
  getConfiguration,
} from '@foxtrotplatform/platform-micro-site-core';
import { AxiosRequestConfig } from 'axios';

import {
  APISubscriptionDetails,
  BillingForm,
  SignUpForm,
  SubscriptionCompletion,
} from '../../components/sign-up/types';
import {
  ApiResponse,
  CompatibleAddonsResponse,
  CreateAccountResponse,
  ProductFamily,
  ResendOtpResponse,
  SignUpResponse,
  SignupV2Request,
  SubscriptionResponse,
  ValidationResponse,
  VerifyOtpResponse,
} from './types';

export const initialSignUp = async (
  form: SignUpForm,
  tierName: string,
  solutionId: string
): Promise<SignUpResponse> => {
  const config = await getConfiguration();
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/signup/initial`,
    method: 'post',
    data: { ...form, tierName, solutionId, dontSendEmail: true },
  };
  const response: ApiResponse<SignUpResponse> = await axiosInstance(request);
  return response.data;
};

export const initialSignUpV2 = async (
  emailAddress: string,
  country: string,
  products: SignupV2Request[]
): Promise<SignUpResponse> => {
  const config = await getConfiguration();
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/signup/v2/initial`,
    method: 'post',
    data: { emailAddress, country, products },
  };
  const response: ApiResponse<SignUpResponse> = await axiosInstance(request);
  return response.data;
};

export const getProductPlans = async (
  country: string,
  email: string
): Promise<ProductFamily[]> => {
  const config = await getConfiguration();
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/product/`,
    method: 'post',
    data: { country, email },
  };
  const response: ApiResponse<ProductFamily[]> = await axiosInstance(request);
  return response.data;
};

export const postOrgDetails = async (
  uuid: string,
  firstName: string,
  lastName: string,
  companyName: string,
  mobileNumber: string,
  orgDomainName?: string
) => {
  let payload = {
    uuid,
    firstName,
    lastName,
    companyName,
    mobileNumber,
  };
  if (orgDomainName) payload['orgDomainName'] = orgDomainName;

  const config = await getConfiguration();
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/signup/v2/organization`,
    method: 'post',
    data: payload,
  };
  const response: ApiResponse<SignUpResponse> = await axiosInstance(request);
  return response.data;
};

export const getSubscription = async (
  requestId: string
): Promise<SubscriptionResponse> => {
  const config = await getConfiguration();
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/signup/subscription/${requestId}`,
    method: 'get',
  };

  const response: ApiResponse<SubscriptionResponse> =
    await axiosInstance(request);
  return response.data;
};

export const verifyBillingAddress = async (form: BillingForm, uuid: string) => {
  const config = await getConfiguration();
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/signup/billing`,
    method: 'post',
    data: { ...form, uuid },
  };
  const response: ApiResponse<ValidationResponse> =
    await axiosInstance(request);
  return response.data;
};

export const uploadTaxDetails = async (
  uuid: string,
  tax: string,
  taxType: TaxTypeIndia,
  taxDocuments?: File[]
) => {
  const config = await getConfiguration();
  const files = new FormData();
  files.append('uuid', uuid);
  files.append('tax', tax);
  files.append('taxType', taxType);
  taxDocuments?.forEach((document) => {
    files.append('files', document);
  });
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/signup/upload`,
    method: 'post',
    data: files,
  };
  const response: ApiResponse<ValidationResponse> =
    await axiosInstance(request);
  return response.data;
};

export const getCompatibleAddons = async (uuid: string) => {
  const config = await getConfiguration();
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/signup/addons?uuid=${uuid}`,
    method: 'get',
  };
  const response: ApiResponse<CompatibleAddonsResponse> =
    await axiosInstance(request);
  return response.data;
};

export const saveAddonToAccount = async (uuid: string, products: string[]) => {
  const config = await getConfiguration();
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/signup/addons`,
    method: 'post',
    data: { uuid, products },
  };
  const response: ApiResponse<CompatibleAddonsResponse> =
    await axiosInstance(request);
  return response.data;
};

export const createAccount = async (
  uuid: string,
  paymentFullfillment: string
) => {
  const config = await getConfiguration();
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/signup/final`,
    method: 'post',
    data: { uuid, payment_id: paymentFullfillment, version: 2 },
  };
  const response: ApiResponse<CreateAccountResponse> =
    await axiosInstance(request);
  return mapAPIKeyProduct(response.data);
};

export const verifyOtp = async (
  email: string,
  code: string
): Promise<VerifyOtpResponse> => {
  const config = await getConfiguration();
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/signup/verify/code`,
    method: 'post',
    data: { email, code },
  };
  const response: ApiResponse<VerifyOtpResponse> = await axiosInstance(request);
  return response.data;
};

export const resendOtp = async (email: string): Promise<ResendOtpResponse> => {
  const config = await getConfiguration();
  const request: AxiosRequestConfig = {
    url: `${config.baseApiUrl}/signup/check/email?q=${email}`,
    method: 'get',
  };

  const response: ApiResponse<ResendOtpResponse> = await axiosInstance(request);
  return response.data;
};

const mapAPIKeyProduct = ({
  keys,
  complete,
  requestId,
}: CreateAccountResponse): SubscriptionCompletion => {
  const apiDetails = keys?.map((key) => {
    const details = key?.solution?.associatedApis?.apiDetails.map(
      ({ name, relativePath }) => {
        return {
          name,
          url: `${key?.solution?.associatedApis?.apiBaseUrl}${relativePath}`,
        };
      }
    );
    const docUrl = key?.docs?.filter((doc) => doc.name === 'docs')[0]?.url;
    return {
      name: key?.solution?.displayAttributes?.displayName,
      token: key.key,
      details,
      docUrl,
    } as APISubscriptionDetails;
  });
  return {
    complete,
    requestId,
    apiDetails,
  };
};
